import PaymentStepsAccordion, { PaymentProcess } from "@components/integration/PaymentStepsAccordian";
import PaymentStepsCard, { PaymentSectionDataProps } from "@components/integration/PaymentStepsCard";
import AwardsSection from "@components/new-ui/AwardsSection";
import CaseStudyFeatureSection, { Feature } from "@components/new-ui/CaseStudyFeatureSection";
import ClientLogos from "@components/new-ui/ClientLogos";
import { container_classname } from "@components/new-ui/constants";
import { CTADarkButtons } from "@components/new-ui/CTAButtons";
import CTASection from "@components/new-ui/CTASection";
import FAQSection, { FAQSectionProps } from "@components/new-ui/FAQSection";
import FeatureInfoCard, { FeatureInfo } from "@components/new-ui/IndustriesSection/FeatureInfoCard";
import Herosection, { HerosectionProps } from "@components/new-ui/IndustriesSection/Herosection";
import ProductSection, { Product } from "@components/new-ui/IndustriesSection/ProductSection";
import IntegrationSection from "@components/new-ui/IntegrationSection";
import NewLayout from "@components/new-ui/Layout";

const featureInfo1: FeatureInfo = {
    mainTitle: "Campaign builder and audience targeting",
    imageUrl: "https://assets.gallabox.com/gb-home/whatsapp-marketing/campaign.png",
    featureInfoList: [
        "Audience segmentation to send targeted messages",
        "Ready-to-use templates for easy setup",
        "Rich and wide variety of media to increase engagement",
        "Broadcast and drip marketing to run personalized campaigns.",
    ],
};

const featureInfo2: FeatureInfo = {
    mainTitle: "Shared Inbox for collaborative customer support",
    imageUrl: "https://assets.gallabox.com/gb-home/whatsapp-marketing/shared-inbox.png",
    featureInfoList: [
        "One collaborative inbox for your entire team",
        "Automatic conversation assignment for faster resolutions",
        "Internal notes and tags for smoother team collaboration",
        "Conversation history to access the full customer context.",
    ],
};

const featureInfo3: FeatureInfo = {
    mainTitle: "Workflow automation and chatbot-driven engagement",
    imageUrl: "https://assets.gallabox.com/gb-home/whatsapp-marketing/workflow-automation.png",
    featureInfoList: [
        "Visual flow builder to automate follow-ups",
        "WhatsApp chatbots to drive human-like conversations",
        "Event triggers to nurture customers to take action",
        "Seamless transfer of bot conversations to human agents.",
    ],
};

const featureInfo4: FeatureInfo = {
    mainTitle: "End-to-end funnel efficiency",
    imageUrl: "https://assets.gallabox.com/gb-home/whatsapp-marketing/efficiency.png",
    featureInfoList: [
        "Targeted broadcast and click-to-WhatsApp ads to drive conversations",
        "Lead capture and qualification through WhatsApp chatbots",
        "Instant responses to reduce drop-offs and shorten sales cycles",
        "Customer re-engagement to encourage repeat purchases",
    ],
};

const productCardData: Array<Product> = [
    {
        title: "Simple platform, substantial impact",
        description: "Drive maximum impact by using WhatsApp for not just promotional activities, but also customer support, order tracking, appointment scheduling, and more.",
        imageUrl: "https://assets.gallabox.com/gb-home/solution/solution-icons/group-call.svg",
        headerBackground: true,
    },
    {
        title: "Personalized conversations",
        description:
            "Catch your prospects and customers on a platform where they are highly active. Even when you are away, set up chatbots that can provide human-like assistance.",
        imageUrl: "https://assets.gallabox.com/gb-home/solution/solution-icons/group-chat.svg",
        headerBackground: true,
    },
    {
        title: "Connect & convert leads instantly",
        description:
            "Leverage the power of the omnipotent channel, WhatsApp. Right from lead capturing to closing deals, do everything on WhatsApp with Gallabox’s marketing automations.",
        imageUrl: "https://assets.gallabox.com/gb-home/solution/solution-icons/campaign.svg",
        headerBackground: true,
    },
    {
        title: "Cost-effective medium",
        description:
            "Unlike traditional platforms, WhatsApp is a low-cost marketing channel. This affordability makes it perfect for businesses of all shapes and sizes across industries.",
        imageUrl: "https://assets.gallabox.com/gb-home/solution/solution-icons/dollar.svg",
        headerBackground: true,
    },
];

const caseStudyFeatureCardData: Array<Feature> = [
    {
        mediaUrl: "https://assets.gallabox.com/gb-home/case-study/pickyourtrail.png",
        chiptitle: "TRAVEL",
        title: "Gallabox powers Pickyourtrail's WhatsApp marketing success",
        statsInfoData: [
            { percentage: "77%", label: "Increase in lead generation" },
            { percentage: "70%", label: "Business growth impact" },
            { percentage: "40%", label: "Higher team productivity" },
        ],
        url: "https://gallabox.com/case-study/pickyourtrail",
        cardColor: false,
    },

    {
        mediaUrl: "https://assets.gallabox.com/gb-home/case-study/rostrum-card-image.png",
        chiptitle: "EDUCATION",
        title: "Gallabox transforms WhatsApp marketing for Rostrum Education",
        statsInfoData: [
            { percentage: "60%", label: "Rise in customer satisfaction" },
            { percentage: "40%", label: "Boost in lead generation" },
            { percentage: "20%", label: "Growth in customer base" },
        ],
        url: "https://gallabox.com/case-study/rostrum-education",
        cardColor: false,
    },
];

const defaultFAQData: FAQSectionProps = {
    title: "FAQs on WhatsApp Marketing Software",
    faqItems: [
        {
            question: "Can I automate marketing campaigns through WhatsApp using this software?",
            answer: (
                <div>
                    Yes, Gallabox’s WhatsApp Marketing Software automates your entire customer journey—from the first touchpoint to post-sale re-engagement. Using a visual workflow
                    builder, create complex automation flows based on behavior, delays, or triggers. It helps save time while boosting conversions.
                </div>
            ),
        },
        {
            question: "Does WhatsApp Marketing Software support multimedia content like images, videos, and voice messages?",
            answer: (
                <>
                    Yes, Gallabox supports rich media like images, videos, PDFs, product catalogs, voice notes, and clickable CTAs. Multimedia content enhances promotions, service
                    showcases, educational content, and storytelling. It increases engagement, open rates, and customer interaction over textual communication.
                </>
            ),
        },
        {
            question: "Can I use WhatsApp Marketing Software for both B2B and B2C marketing?",
            answer: (
                <p>
                    Yes, Gallabox works for both B2B and B2C marketing. For B2C, use it for product recommendations, delivery updates, and promotions. For B2B, leverage it for lead
                    nurturing, follow-ups, and webinars. Segmentation and personalization features ensure tailored messaging for both business types.
                </p>
            ),
        },
        {
            question: "Can I segment my audience and send targeted messages with WhatsApp Marketing Software?",
            answer: (
                <div>
                    Yes, Gallabox allows segmentation based on user behavior, tags, location, purchase history, and more. Send targeted messages to different customer groups, like
                    new leads or repeat buyers. This personalization increases engagement, conversion, and builds stronger relationships with your audience.
                </div>
            ),
        },
        {
            question: "Can WhatsApp Marketing Software be integrated with my eCommerce platform for product promotions?",
            answer: (
                <div>
                    Yes, Gallabox integrates with platforms like Shopify, Magento, and custom solutions via API. These integrations trigger WhatsApp messages based on customer
                    behavior, like cart abandonment or order confirmations. You can send promotions, updates, and recommendations seamlessly.
                </div>
            ),
        },
        {
            question: "Does WhatsApp Marketing Software support the creation of automated responses or chatbots?",
            answer: (
                <>
                    Yes, Gallabox has a powerful chatbot builder for creating intelligent automated responses 24/7. Set up FAQs, order tracking, lead capture, and appointment
                    scheduling. These bots improve response times and customer satisfaction, plus they can hand off to human agents when needed.
                </>
            ),
        },
        {
            question: "How secure is my customer data when using WhatsApp Marketing Software?",
            answer: (
                <p>
                    Gallabox prioritizes security, using WhatsApp&apos;s Business API for encrypted communication. It follows best practices for data protection, including GDPR
                    compliance, role-based access, and secure data storage. Your customer data is kept secure, with full transparency and no sharing with unauthorized parties.
                </p>
            ),
        },
        {
            question: "Can I use WhatsApp Marketing Software for sending promotions, discounts, and offers?",
            answer: (
                <p>
                    Yes, WhatsApp is perfect for sending promotions, discounts, and offers. Gallabox allows you to send customized campaigns to customer segments, including product
                    images, discount codes, and CTA buttons. You can even A/B test different formats to find the best-performing offers.
                </p>
            ),
        },
        {
            question: "How do I ensure compliance with WhatsApp's policies and guidelines while using marketing software?",
            answer: (
                <p>
                    Gallabox is built on WhatsApp&apos;s Business API, ensuring compliance with their guidelines. The platform helps you create compliant message templates, secure
                    opt-ins, and follow proper messaging etiquette, reducing the risk of penalties. In-app prompts guide you to stay compliant at all times.
                </p>
            ),
        },
        {
            question: "Can WhatsApp Marketing Software be used to run WhatsApp ads or Click-to-WhatsApp campaigns?",
            answer: (
                <div>
                    Gallabox supports Click-to-WhatsApp campaigns, though it doesn’t create Facebook/Instagram ads. Integrate your ad campaigns to direct users to WhatsApp, where
                    conversations can be automated or handled live. Track and optimize performance based on metrics like open rates and replies.
                </div>
            ),
        },
        {
            question: "What kind of customer support is available if I face issues with WhatsApp Marketing Software?",
            answer: (
                <div>
                    Gallabox provides multi-channel support like live chat, email, dedicated account managers, and documentation. For enterprise users, personalized onboarding and
                    1:1 strategy sessions are available. The support team is known for fast responses, expert knowledge, and helping resolve issues efficiently.
                </div>
            ),
        },
    ],
};

const defaultPaymentSectionData: PaymentSectionDataProps[] = [
    {
        imageUrl: "https://assets.gallabox.com/gb-home/whatsapp-marketing/sign-up.gif",
        title: "Sign up on Gallabox",
        description: "Create a Gallabox account and connect your official WhatsApp number to it. Get your business verified with Facebook Business Manager.",
    },
    {
        imageUrl: "https://assets.gallabox.com/gb-home/whatsapp-marketing/optimize-profile.png",
        title: "Optimize your profile",
        description: "Upload your brand kit to reflect brand identity, and import your contacts.",
    },
    {
        imageUrl: "https://assets.gallabox.com/gb-home/whatsapp-marketing/access-tool.png",
        title: "Access Gallabox’s tools",
        description: "Set up all necessary features such as chatbots, drip marketing sequences, broadcasts, integrations, etc., for your marketing campaigns.",
    },
    {
        imageUrl: "https://assets.gallabox.com/gb-home/whatsapp-marketing/user-access.png",
        title: "Provide user access",
        description: "Assign team roles such as Owner, Admin, and Member to streamline operations.",
    },
];

const pricingProcessAccordian: PaymentProcess = {
    title: "How to integrate WhatsApp payments in 4 easy steps?",
    ProcessSteps: [
        {
            imageUrl: "https://assets.gallabox.com/gb-home/whatsapp-marketing/sign-up.gif",
            title: "Sign up on Gallabox",
            description: "Create a Gallabox account and connect your official WhatsApp number to it. Get your business verified with Facebook Business Manager.",
        },
        {
            imageUrl: "https://assets.gallabox.com/gb-home/whatsapp-marketing/optimize-profile.png",
            title: "Optimize your profile",
            description: "Upload your brand kit to reflect brand identity, and import your contacts.",
        },
        {
            imageUrl: "https://assets.gallabox.com/gb-home/whatsapp-marketing/access-tool.png",
            title: "Access Gallabox’s tools",
            description: "Set up all necessary features such as chatbots, drip marketing sequences, broadcasts, integrations, etc., for your marketing campaigns.",
        },
        {
            imageUrl: "https://assets.gallabox.com/gb-home/whatsapp-marketing/user-access.png",
            title: "Provide user access",
            description: "Assign team roles such as Owner, Admin, and Member to streamline operations.",
        },
    ],
};

const herosectionData: HerosectionProps = {
    chipTitle: "WHATSAPP MARKETING SOFTWARE",
    title: "AI-Powered WhatsApp Marketing — Target, Broadcast, Convert",
    description: "Grow faster with automated and personalized WhatsApp campaigns. Build and nurture a strong pipeline with Gallabox’s marketing automation software.",
    primaryButtonText: "Start Free Trial",
    imageUrl: "https://assets.gallabox.com/gb-home/whatsapp-marketing/wa-marketing-hero-image.png",
};

const WhatsappMarketingSoftware: React.FC = () => {
    return (
        <NewLayout
            title={"WhatsApp Marketing Platform — AI-Powered Campaigns & Broadcast | Gallabox"}
            description={"Run smarter WhatsApp campaigns with AI targeting, intelligent broadcast & automated follow-ups. Personalize at scale. Start free trial."}
            canonical={"https://gallabox.com/whatsapp-marketing"}
            schema={{
                heroOrOgImageAlt: "whatsapp-marketing-image",
                heroOrOgImageDescription:
                    "Grow faster with automated and personalized WhatsApp campaigns. Build and nurture a strong pipeline with Gallabox’s marketing automation software.",
                heroOrOgImageUrl: "https://assets.gallabox.com/gb-home/whatsapp-marketing/wa-marketing-hero-image.png",
                faq: defaultFAQData.faqItems,
                softwareFeatureName: "whatsapp-marketing",
            }}
        >
            <Herosection herosection={herosectionData} />
            <section className="mt-12 mb-20">
                <div className="w-full">
                    <h3 className="text-base text-center font-normal py-2">Trusted by 10,000+ businesses</h3>
                    <ClientLogos />
                </div>
            </section>
            <ProductSection title="Why use WhatsApp for marketing?" productCardData={productCardData} />
            <h5 className={`${container_classname} mt-20 text-5xl text-center`}>Qualify and engage leads with Gallabox’s WhatsApp marketing software</h5>
            <section>
                <FeatureInfoCard {...featureInfo1} />
                <FeatureInfoCard {...featureInfo2} />
                <FeatureInfoCard {...featureInfo3} />
                <FeatureInfoCard {...featureInfo4} />
            </section>
            <section className="lg:block hidden">
                <PaymentStepsCard
                    backgroundColor=""
                    imageBackgroundColor=""
                    paymentSectionData={defaultPaymentSectionData}
                    title="How to use Gallabox’s WhatsApp marketing software"
                />
            </section>
            <PaymentStepsAccordion paymentStepsData={pricingProcessAccordian} />
            <section>
                <CaseStudyFeatureSection features={caseStudyFeatureCardData} />
            </section>
            <FAQSection faqData={defaultFAQData} />
            <IntegrationSection isLight={true} />
            <AwardsSection />
            <CTASection title="Engage smarter, sell better with Gallabox’s WhatsApp marketing software!" customCTAButton={<CTADarkButtons primaryButtonText="Get free trial" />} />
        </NewLayout>
    );
};

export default WhatsappMarketingSoftware;
